import { useEffect, useMemo, useState } from "react";
import {
  FOUNDAYO_DOSE_LABELS,
  FOUNDAYO_DOSE_STEPS,
  canRecordMaxToleratedDose,
  evaluateFoundayoReview,
  foundayoDoseOptions,
  foundayoGpNotificationRequired,
  foundayoRecordBlockers,
  type FoundayoDoseStep,
  type FoundayoGpConsent,
  type FoundayoGpNotifyMethod,
  type FoundayoMeasurementMethod,
  type FoundayoReviewMethod,
  type FoundayoReferral,
  type FoundayoReviewResult,
  type FoundayoStopReason,
  type MaxToleratedReason,
} from "@/features/aht-weight/foundayoReview";

type YesNo = "yes" | "no" | "";

export interface FoundayoPanelValues {
  currentWeightKg: string;
  bmi: string;
  waistCm: string;
  pulse: string;
  bpSystolic: string;
  bpDiastolic: string;
  pulsePersistent: YesNo;
  hypotension: YesNo;
  moodConcern: YesNo;
  selfHarm: YesNo;
  binge: YesNo;
  vomiting: YesNo;
  laxative: YesNo;
  exercise: YesNo;
  pancreatitis: "none" | "suspected" | "confirmed";
  vision: "none" | "sudden" | "naion";
  fluids: YesNo;
  gallbladder: YesNo;
  gallbladderRed: YesNo;
  pregnant: YesNo;
  contraception: "" | "not_applicable" | "effective_in_place" | "unwilling_or_unable";
  medicinesReconciled: boolean;
  type2Diabetes: YesNo;
  hba1cDate: string;
  currentStep: "" | FoundayoDoseStep;
  doseStartDate: string;
  capped: YesNo;
  doseDecision: string;
  recordMtd: boolean;
  mtdReason: "" | MaxToleratedReason;
  reviewMethod: "" | FoundayoReviewMethod;
  measurementMethod: "" | FoundayoMeasurementMethod;
  gpConsent: "" | FoundayoGpConsent;
  gpNotifyMethod: "" | FoundayoGpNotifyMethod;
  gpNotifyDate: string;
  gpDeclineReason: string;
}

export interface FoundayoPanelState {
  values: FoundayoPanelValues;
  result: FoundayoReviewResult;
  maxToleratedDoseDate: string | null;
  /** Reasons the record cannot be saved yet. Empty when it can. */
  saveBlockers: string[];
}

interface Props {
  isDarkMode: boolean;
  /** Weight recorded at first supply on the treatment plan: the fixed baseline for % change. */
  initialWeightKg: number | null;
  /** Date waist was last recorded, from earlier saved reviews or the plan. */
  lastWaistDate: string | null;
  /** Maximum tolerated dose date already recorded on an earlier review. */
  priorMaxToleratedDoseDate: string | null;
  onChange: (state: FoundayoPanelState) => void;
}

const STOP_TEXT: Record<FoundayoStopReason, string> = {
  bmi_below_floor: "BMI is below 22.5 kg/m²: stop treatment.",
  suspected_pancreatitis: "Suspected acute pancreatitis: stop immediately and do not supply further.",
  confirmed_pancreatitis: "Pancreatitis confirmed: stop. Never restart.",
  self_harm_thoughts: "Thoughts of self harm: do not supply further.",
  naion_confirmed: "NAION confirmed: stop orforglipron.",
  pregnancy: "Pregnancy: stop treatment.",
  efficacy_below_threshold: "Less than 5% weight loss from the initial weight after 6 months at the maximum tolerated dose: discontinue.",
  disordered_eating_features: "Disordered eating features: stop, do not supply further.",
  contraception_unwilling_or_unable: "Unwilling or unable to use effective contraception: supply cannot continue.",
};

const REFERRAL_TEXT: Record<FoundayoReferral, string> = {
  same_day_emergency_assessment: "Refer for same day emergency assessment. Notify the GP the same day. Record the referral, the route used and the outcome once known.",
  same_day_medical_assessment: "Refer for same day medical assessment.",
  same_day_eye_assessment: "Refer urgently for same day emergency eye service or emergency department assessment.",
  same_day_assessment_fluids: "Same day assessment where fluids cannot be kept down.",
  gp_gallbladder: "Refer to the GP for imaging and follow up.",
  gp_same_day_gallbladder: "Same day assessment: fever, jaundice or severe right upper quadrant pain.",
  gp_tachycardia: "Refer to the GP: resting pulse persistently above 100 bpm.",
  gp_hypotension: "Refer to the GP: symptomatic hypotension.",
  gp_eating_disorder_with_consent: "Refer to the GP with the patient's consent and signpost to eating disorder support.",
};

const FLAG_TEXT: Record<string, string> = {
  hba1c_over_6_months_old: "HbA1c is more than 6 months old (or not recorded): arrange a repeat with the GP or diabetes team.",
  waist_due: "Waist circumference is due (at least every 3 months).",
  mood_concern_review_and_gp_referral: "Mood concern reported: review, consider discontinuation, and refer to the GP with consent.",
};

const MTD_REASONS: { id: MaxToleratedReason; label: string }[] = [
  { id: "at_17_2mg", label: "Patient is at the 17.2 mg maximum" },
  { id: "failed_increase", label: "An increase was tried and not tolerated" },
  { id: "agreed_hold_adverse_effects", label: "Dose held by agreement because of adverse effects" },
  { id: "held_for_interacting_medicine", label: "Dose held only because of an interacting medicine (not a maximum tolerated dose)" },
];

const num = (v: string): number | null => {
  const n = Number(v);
  return v.trim() !== "" && Number.isFinite(n) ? n : null;
};
const yes = (v: YesNo) => v === "yes";
const todayIso = () => new Date().toISOString().slice(0, 10);

export const emptyFoundayoValues: FoundayoPanelValues = {
  currentWeightKg: "", bmi: "", waistCm: "", pulse: "", bpSystolic: "", bpDiastolic: "",
  pulsePersistent: "", hypotension: "", moodConcern: "", selfHarm: "", binge: "", vomiting: "", laxative: "", exercise: "",
  pancreatitis: "none", vision: "none", fluids: "", gallbladder: "", gallbladderRed: "", pregnant: "",
  contraception: "", medicinesReconciled: false, type2Diabetes: "", hba1cDate: "",
  currentStep: "", doseStartDate: "", capped: "", doseDecision: "", recordMtd: false, mtdReason: "",
  reviewMethod: "", measurementMethod: "", gpConsent: "", gpNotifyMethod: "", gpNotifyDate: "", gpDeclineReason: "",
};

export function AhtFoundayoReviewPanel({ isDarkMode: dm, initialWeightKg, lastWaistDate, priorMaxToleratedDoseDate, onChange }: Props) {
  const [v, setV] = useState<FoundayoPanelValues>(emptyFoundayoValues);
  const set = <K extends keyof FoundayoPanelValues>(k: K, value: FoundayoPanelValues[K]) => setV((p) => ({ ...p, [k]: value }));

  const computed = useMemo<FoundayoPanelState>(() => {
    const maxToleratedDoseDate = v.recordMtd && v.mtdReason !== "" && v.currentStep !== "" && canRecordMaxToleratedDose(v.currentStep, v.mtdReason) ? todayIso() : priorMaxToleratedDoseDate;
    const answer = (a: YesNo): boolean | null => (a === "" ? null : yes(a));
    const result = evaluateFoundayoReview({
      bmi: num(v.bmi),
      initialWeightKg,
      currentWeightKg: num(v.currentWeightKg),
      restingPulse: num(v.pulse),
      bloodPressureRecorded: num(v.bpSystolic) !== null && num(v.bpDiastolic) !== null,
      pulsePersistentlyAbove100: yes(v.pulsePersistent),
      symptomaticHypotension: yes(v.hypotension),
      suspectedPancreatitis: v.pancreatitis === "suspected",
      confirmedPancreatitis: v.pancreatitis === "confirmed",
      selfHarmThoughts: yes(v.selfHarm),
      suddenVisionChange: v.vision !== "none",
      naionConfirmed: v.vision === "naion",
      cannotKeepFluidsDown: yes(v.fluids),
      gallbladderSymptoms: yes(v.gallbladder),
      gallbladderRedFlags: yes(v.gallbladderRed),
      pregnant: yes(v.pregnant),
      contraceptionStatus: v.contraception === "" ? null : v.contraception,
      moodConcern: yes(v.moodConcern),
      medicinesReconciled: v.medicinesReconciled,
      disorderedEatingFeatures: false,
      eatingScreen: { bingeEating: answer(v.binge), selfInducedVomiting: answer(v.vomiting), laxativeOrDiureticMisuse: answer(v.laxative), compulsiveExercise: answer(v.exercise) },
      type2Diabetes: yes(v.type2Diabetes),
      lastHba1cDate: v.hba1cDate || null,
      lastWaistDate: num(v.waistCm) !== null ? todayIso() : lastWaistDate,
      maxToleratedDoseDate,
    });
    const saveBlockers = [...result.blockers];
    if (v.pregnant === "" || v.selfHarm === "" || v.fluids === "" || v.gallbladder === "") saveBlockers.push("Answer the pregnancy, self harm, fluids and gallbladder questions.");
    if (v.currentStep === "") saveBlockers.push("Select the current dose.");
    if (v.capped === "") saveBlockers.push("Answer the interacting medicine (dose cap) question.");
    if (v.recordMtd && (v.mtdReason === "" || v.currentStep === "" || !canRecordMaxToleratedDose(v.currentStep, v.mtdReason))) {
      saveBlockers.push("The maximum tolerated dose cannot be recorded on those grounds.");
    }
    if (result.stopSupply) {
      if (v.doseDecision !== "stop") saveBlockers.push("Supply must stop: record the stop decision.");
    } else if (v.doseDecision === "") saveBlockers.push("Record the dose decision.");
    saveBlockers.push(...foundayoRecordBlockers({
      reviewMethod: v.reviewMethod, measurementMethod: v.measurementMethod, doseDecision: v.doseDecision, stopSupply: result.stopSupply,
      gpConsent: v.gpConsent, gpNotifyMethod: v.gpNotifyMethod, gpNotifyDate: v.gpNotifyDate, gpDeclineReason: v.gpDeclineReason,
    }));
    return { values: v, result, maxToleratedDoseDate, saveBlockers };
  }, [v, initialWeightKg, lastWaistDate, priorMaxToleratedDoseDate]);

  const stateKey = JSON.stringify([computed.values, computed.saveBlockers, computed.maxToleratedDoseDate]);
  useEffect(() => onChange(computed), [stateKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const doseInfo = useMemo(
    () => (v.currentStep === "" ? null : foundayoDoseOptions(v.currentStep, v.doseStartDate || null, yes(v.capped))),
    [v.currentStep, v.doseStartDate, v.capped],
  );

  const input = `w-full px-3 py-2 text-sm rounded-lg border ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"}`;
  const label = `block text-xs font-medium mb-1 ${dm ? "text-gray-300" : "text-gray-700"}`;
  const text = dm ? "text-gray-200" : "text-gray-800";
  const box = (tone: "red" | "amber" | "blue") =>
    `px-3 py-2 rounded-lg border text-sm ${
      tone === "red" ? (dm ? "bg-red-900/20 border-red-700 text-red-200" : "bg-red-50 border-red-300 text-red-800")
      : tone === "amber" ? (dm ? "bg-amber-900/20 border-amber-700 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-800")
      : (dm ? "bg-blue-900/20 border-blue-700 text-blue-200" : "bg-blue-50 border-blue-200 text-blue-800")
    }`;

  const YesNoField = ({ k, q }: { k: keyof FoundayoPanelValues; q: string }) => (
    <div className="flex items-start gap-4">
      <span className={`text-sm flex-1 ${text}`}>{q}</span>
      <div className="flex gap-3 shrink-0">
        {(["yes", "no"] as const).map((opt) => (
          <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
            <input type="radio" name={`fdy-${String(k)}`} checked={v[k] === opt} onChange={() => set(k, opt as never)} className="accent-blue-600" />
            <span className={`text-sm ${dm ? "text-gray-300" : "text-gray-700"}`}>{opt === "yes" ? "Yes" : "No"}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const r = computed.result;
  const pct = r.percentChangeFromInitial;

  return (
    <div className="space-y-4" data-testid="foundayo-review-panel">
      <div className={box("blue")}>
        Foundayo® (orforglipron) tablets. Monthly review is mandatory. Weight change is measured against the initial weight recorded at first supply
        ({initialWeightKg === null ? "not recorded" : `${initialWeightKg} kg`}).
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>How was this review carried out?</label>
          <select className={input} value={v.reviewMethod} onChange={(e) => set("reviewMethod", e.target.value as FoundayoPanelValues["reviewMethod"])} data-testid="foundayo-review-method">
            <option value="">Select</option>
            <option value="live_video">Live video (real time, two way audio and video)</option>
            <option value="in_person">In person at the registered premises</option>
          </select>
        </div>
        <div>
          <label className={label}>How were height and weight verified?</label>
          <select className={input} value={v.measurementMethod} onChange={(e) => set("measurementMethod", e.target.value as FoundayoPanelValues["measurementMethod"])} data-testid="foundayo-measurement-method">
            <option value="">Select</option>
            <option value="in_person_premises">Measured in person at the registered premises</option>
            <option value="live_video_observed">Observed on live video, scales and measure visible throughout</option>
            <option value="gp_or_hcp_supplied">Supplied directly by the GP or another registered professional</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><label className={label}>Verified weight today (kg)</label><input className={input} value={v.currentWeightKg} onChange={(e) => set("currentWeightKg", e.target.value)} /></div>
        <div><label className={label}>BMI today</label><input className={input} value={v.bmi} onChange={(e) => set("bmi", e.target.value)} /></div>
        <div><label className={label}>Waist (cm), at least every 3 months</label><input className={input} value={v.waistCm} onChange={(e) => set("waistCm", e.target.value)} /></div>
        <div><label className={label}>Resting pulse (bpm)</label><input className={input} value={v.pulse} onChange={(e) => set("pulse", e.target.value)} /></div>
        <div><label className={label}>Systolic BP (mmHg)</label><input className={input} value={v.bpSystolic} onChange={(e) => set("bpSystolic", e.target.value)} /></div>
        <div><label className={label}>Diastolic BP (mmHg)</label><input className={input} value={v.bpDiastolic} onChange={(e) => set("bpDiastolic", e.target.value)} /></div>
      </div>
      {pct !== null && (
        <p className={`text-sm ${text}`} data-testid="foundayo-percent-change">
          Change from initial weight: {pct > 0 ? "+" : ""}{pct.toFixed(1)}%
        </p>
      )}

      <div className="space-y-2">
        <YesNoField k="pulsePersistent" q="Is the resting pulse persistently above 100 bpm, with or without palpitations, syncope or presyncope?" />
        <YesNoField k="hypotension" q="Symptomatic hypotension?" />
        <YesNoField k="moodConcern" q="New or worsening low mood, anxiety or unusual change in behaviour?" />
        <YesNoField k="selfHarm" q="Any thoughts of self harm?" />
      </div>

      <div className="space-y-2">
        <p className={`text-xs font-semibold ${text}`}>Disordered eating screen: ask all four directly at every review</p>
        <YesNoField k="binge" q="Binge eating?" />
        <YesNoField k="vomiting" q="Self induced vomiting?" />
        <YesNoField k="laxative" q="Laxative or diuretic use for weight control?" />
        <YesNoField k="exercise" q="Compulsive exercise?" />
      </div>

      <div className="space-y-2">
        <div>
          <label className={label}>Pancreatitis</label>
          <select className={input} value={v.pancreatitis} onChange={(e) => set("pancreatitis", e.target.value as FoundayoPanelValues["pancreatitis"])}>
            <option value="none">No symptoms</option>
            <option value="suspected">Suspected acute pancreatitis</option>
            <option value="confirmed">Confirmed acute pancreatitis</option>
          </select>
        </div>
        <div>
          <label className={label}>Vision</label>
          <select className={input} value={v.vision} onChange={(e) => set("vision", e.target.value as FoundayoPanelValues["vision"])}>
            <option value="none">No change</option>
            <option value="sudden">Sudden change or loss of vision</option>
            <option value="naion">NAION confirmed</option>
          </select>
        </div>
        <YesNoField k="fluids" q="Unable to keep fluids down?" />
        <YesNoField k="gallbladder" q="Gallbladder symptoms?" />
        {yes(v.gallbladder) && <YesNoField k="gallbladderRed" q="Fever, jaundice or severe right upper quadrant pain?" />}
        <YesNoField k="pregnant" q="Pregnant, or suspected pregnancy?" />
        <div>
          <label className={label}>Pregnancy and contraception status</label>
          <select className={input} value={v.contraception} onChange={(e) => set("contraception", e.target.value as FoundayoPanelValues["contraception"])}>
            <option value="">Select</option>
            <option value="not_applicable">Not applicable</option>
            <option value="effective_in_place">Effective contraception in place; 30 day cover after each dose increase explained</option>
            <option value="unwilling_or_unable">Unwilling or unable to use effective contraception</option>
          </select>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={v.medicinesReconciled} onChange={(e) => set("medicinesReconciled", e.target.checked)} className="accent-blue-600" />
          <span className={`text-sm ${text}`}>Full medicines reconciliation completed</span>
        </label>
        <YesNoField k="type2Diabetes" q="Does the patient have type 2 diabetes?" />
        {yes(v.type2Diabetes) && (
          <div><label className={label}>Date of most recent HbA1c</label><input type="date" className={input} value={v.hba1cDate} onChange={(e) => set("hba1cDate", e.target.value)} /></div>
        )}
      </div>

      {r.stopReasons.length > 0 && (
        <div className={box("red")} role="alert" data-testid="foundayo-stop">
          {r.stopReasons.map((s) => <p key={s}>{STOP_TEXT[s]}</p>)}
          {r.neverRestart && <p className="font-semibold">Permanent flag: never restart orforglipron.</p>}
        </div>
      )}
      {r.referrals.length > 0 && (
        <div className={box("red")} role="alert" data-testid="foundayo-referrals">
          {r.referrals.map((x) => <p key={x}>{REFERRAL_TEXT[x]}</p>)}
        </div>
      )}
      {r.flags.length > 0 && (
        <div className={box("amber")} data-testid="foundayo-flags">
          {r.flags.map((f) => <p key={f}>{FLAG_TEXT[f] ?? f}</p>)}
        </div>
      )}
      {r.efficacyReviewDue && !r.stopSupply && (
        <div className={box("amber")}>Six month efficacy review is due: at least 5% loss from the initial weight is required to continue.</div>
      )}

      <div className="space-y-2">
        <p className={`text-xs font-semibold ${text}`}>Dose</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Current dose</label>
            <select className={input} value={v.currentStep} onChange={(e) => { set("currentStep", e.target.value as FoundayoPanelValues["currentStep"]); set("doseDecision", ""); }}>
              <option value="">Select</option>
              {FOUNDAYO_DOSE_STEPS.map((s) => <option key={s} value={s}>{FOUNDAYO_DOSE_LABELS[s]}</option>)}
            </select>
          </div>
          <div><label className={label}>Date this dose was started</label><input type="date" className={input} value={v.doseStartDate} onChange={(e) => { set("doseStartDate", e.target.value); set("doseDecision", ""); }} /></div>
        </div>
        <YesNoField k="capped" q="Taking a strong CYP3A4 inhibitor (ketoconazole, clarithromycin, itraconazole) or ciclosporin? Maximum 9 mg." />
        {doseInfo && (
          <div className="space-y-1">
            {r.stopSupply ? (
              <label className="flex items-center gap-2"><input type="radio" name="fdy-dose-decision" checked={v.doseDecision === "stop"} onChange={() => set("doseDecision", "stop")} className="accent-blue-600" /><span className={`text-sm ${text}`}>Stop supply</span></label>
            ) : (
              doseInfo.options.map((o) => (
                <label key={o.decision} className="flex items-center gap-2">
                  <input type="radio" name="fdy-dose-decision" checked={v.doseDecision === o.decision} onChange={() => set("doseDecision", o.decision)} className="accent-blue-600" />
                  <span className={`text-sm ${text}`}>
                    {o.decision === "hold" ? "Hold at" : o.decision === "reduce" ? "Reduce to" : "Increase to"} {FOUNDAYO_DOSE_LABELS[o.step]}
                  </span>
                </label>
              ))
            )}
            {doseInfo.nextIncreaseBlockedUntil && <p className={`text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}>The next increase is permitted from {doseInfo.nextIncreaseBlockedUntil}: at least 30 days at each dose.</p>}
            {doseInfo.nextIncreaseBlockedByCap && <p className={`text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}>No increase above 9 mg with an interacting inhibitor.</p>}
          </div>
        )}
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={v.recordMtd} onChange={(e) => set("recordMtd", e.target.checked)} className="accent-blue-600" />
          <span className={`text-sm ${text}`}>Record the maximum tolerated dose today (starts the 6 month efficacy clock)</span>
        </label>
        {v.recordMtd && (
          <div>
            <label className={label}>Reason</label>
            <select className={input} value={v.mtdReason} onChange={(e) => set("mtdReason", e.target.value as FoundayoPanelValues["mtdReason"])}>
              <option value="">Select</option>
              {MTD_REASONS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
        )}
        {computed.maxToleratedDoseDate && <p className={`text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}>Maximum tolerated dose recorded on {computed.maxToleratedDoseDate}.</p>}
      </div>

      {foundayoGpNotificationRequired(v.doseDecision, r.stopSupply) && (
        <div className="space-y-2" data-testid="foundayo-gp-notification">
          <p className={`text-xs font-semibold ${text}`}>GP notification (required at every dose change and on discontinuation)</p>
          <div>
            <label className={label}>Did the patient consent to GP notification?</label>
            <select className={input} value={v.gpConsent} onChange={(e) => set("gpConsent", e.target.value as FoundayoPanelValues["gpConsent"])} data-testid="foundayo-gp-consent">
              <option value="">Select</option>
              <option value="given">Yes, consent given</option>
              <option value="declined_or_no_gp">No: declined, or no registered GP</option>
            </select>
          </div>
          {v.gpConsent === "given" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>How was the GP notified?</label>
                <select className={input} value={v.gpNotifyMethod} onChange={(e) => set("gpNotifyMethod", e.target.value as FoundayoPanelValues["gpNotifyMethod"])}>
                  <option value="">Select</option>
                  <option value="template_letter">Template letter</option>
                  <option value="secure_email">Secure email</option>
                  <option value="other_secure">Another agreed secure method</option>
                </select>
              </div>
              <div><label className={label}>Date the GP was notified</label><input type="date" className={input} value={v.gpNotifyDate} onChange={(e) => set("gpNotifyDate", e.target.value)} /></div>
            </div>
          )}
          {v.gpConsent === "declined_or_no_gp" && (
            <div>
              <label className={label}>Reason, the specific risks considered, and why supply was still considered safe (or the decision not to supply)</label>
              <textarea className={input} rows={3} value={v.gpDeclineReason} onChange={(e) => set("gpDeclineReason", e.target.value)} />
            </div>
          )}
        </div>
      )}

      {computed.saveBlockers.length > 0 && (
        <ul className={`${box("amber")} list-disc pl-6`} data-testid="foundayo-blockers">
          {computed.saveBlockers.map((b) => <li key={b}>{b}</li>)}
        </ul>
      )}
    </div>
  );
}
